import React from 'react';
import { styled } from '@compiled/react';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
	useRegisterCommandSections,
	TruncateWithTooltipOnHover,
} from '@atlassian/jira-command-palette';
import {
	COMMAND_PALETTE_SECTIONS_PRIORITY,
	getCommandPaletteIssueActionsHeader,
	COMMAND_PALETTE_ISSUE_ACTIONS,
} from '@atlassian/jira-command-palette-common';
import { IssueTag } from '@atlassian/jira-command-palette-issue-actions-common';
import AsyncIcon from '@atlassian/jira-common-components-async-icon';
import { componentWithFF } from '@atlassian/jira-feature-flagging-utils';
import { useIntl } from '@atlassian/jira-intl';
import { useIssueTypeField } from '@atlassian/jira-issue-field-issue-type';
import { useSummaryField } from '@atlassian/jira-issue-field-summary';
import { messages } from './messages';
import type { RenderCommandPaletteHeaderProps } from './types';

const RenderCommandPaletteHeaderOld = ({ issueKey }: RenderCommandPaletteHeaderProps) => {
	const [{ value: summary }] = useSummaryField({ issueKey });
	const [{ value: issueType }] = useIssueTypeField({ issueKey });
	const { formatMessage } = useIntl();

	const issueTypeIconUrl = issueType?.iconUrl;
	const issueTypeName = issueType?.name;

	const renderName = () => (
		<CompalSectionHeaderContainer>
			<IssueActionLabel>{formatMessage(messages.issueActions)}</IssueActionLabel>
			{!!issueTypeIconUrl && (
				<IssueIconWrapper>
					<AsyncIcon url={issueTypeIconUrl} alt={issueTypeName} />
				</IssueIconWrapper>
			)}
			<TruncateWithTooltipOnHover>
				<IssueSummaryContainer>
					{issueKey} {summary}
				</IssueSummaryContainer>
			</TruncateWithTooltipOnHover>
		</CompalSectionHeaderContainer>
	);

	const sectionId = getCommandPaletteIssueActionsHeader(issueKey);

	useRegisterCommandSections(
		{
			[sectionId]: {
				renderName,
				priority: COMMAND_PALETTE_SECTIONS_PRIORITY.issueActions,
				actionCategory: COMMAND_PALETTE_ISSUE_ACTIONS,
			},
		},
		[issueKey, summary, issueTypeIconUrl],
	);

	return null;
};

const RenderCommandPaletteHeaderNew = ({ issueKey }: RenderCommandPaletteHeaderProps) => {
	const { formatMessage } = useIntl();
	const [{ value: summary }] = useSummaryField({ issueKey });

	// Since IssueTag is another component and not part of renderName, it cannot be traversed by command palette
	// to detect the header text and make it searchable. We duplicate the issue key and summary and visually hide it
	const renderName = () => (
		<Inline alignBlock="center" space="space.050">
			<Box xcss={issueActionsLabelStyles}>{formatMessage(messages.issueActions)}</Box>
			<div
				hidden
				data-testid="issue.views.issue-details.register-command-palette-header.visually-hidden"
			>
				{issueKey} {summary}
			</div>
			<IssueTag issueKey={issueKey} />
		</Inline>
	);

	const sectionId = getCommandPaletteIssueActionsHeader(issueKey);
	useRegisterCommandSections(
		{
			[sectionId]: {
				renderName,
				priority: COMMAND_PALETTE_SECTIONS_PRIORITY.issueActions,
				actionCategory: COMMAND_PALETTE_ISSUE_ACTIONS,
			},
		},
		[issueKey],
	);

	return null;
};

export const RenderCommandPaletteHeader = componentWithFF(
	'compal-issue-actions-very-high-priority_f5w5r',
	RenderCommandPaletteHeaderNew,
	RenderCommandPaletteHeaderOld,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueActionLabel = styled.div({
	flexShrink: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueIconWrapper = styled.div({
	display: 'flex',
	flexShrink: 0,
	marginLeft: token('space.075', '6px'),
	marginRight: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CompalSectionHeaderContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueSummaryContainer = styled.div({
	fontSize: '12px',
});

const issueActionsLabelStyles = xcss({
	fontSize: '14px',
	color: 'color.text.subtlest',
	// @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'textWrap' does not exist in type 'SafeCSSObject'.
	textWrap: 'nowrap',
});
